import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection as Section } from '../components/layout/LegalLayout';

const LAST_UPDATED = 'June 19, 2026';

const SEVERITY = {
  instant: { label: 'Instant Ban',    cls: 'bg-red-500/12 border-red-500/25 text-red-300' },
  warning: { label: 'Warning → Ban',  cls: 'bg-amber-500/12 border-amber-500/25 text-amber-300' },
  review:  { label: 'Manual Review',  cls: 'bg-sky-500/12 border-sky-500/25 text-sky-300' },
};

function RuleItem({ severity, children }) {
  const s = SEVERITY[severity] || SEVERITY.review;
  return (
    <div className="flex flex-col sm:flex-row items-start gap-2.5 sm:gap-4 py-3.5
                    border-b border-white/[0.05] last:border-0">
      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border
                        whitespace-nowrap flex-shrink-0 sm:w-32 sm:text-center ${s.cls}`}>
        {s.label}
      </span>
      <p className="text-sm text-gray-300 leading-relaxed">{children}</p>
    </div>
  );
}

export default function RulesPage() {
  return (
    <LegalLayout
      title="Rules & Acceptable Use Policy"
      lastUpdated={LAST_UPDATED}
      back={{ to: '/terms', label: 'Back to Terms of Service' }}
      intro={
        <p>
          This page explains specifically what is and isn't allowed when using MortisEngine, and
          how violations are enforced. This is a companion document to our{' '}
          <Link to="/terms" className="link underline">Terms of Service</Link>,
          which remains the governing legal agreement.
        </p>
      }
    >
      <Section title="How automated enforcement works">
        <p className="text-gray-400">
          The MortisEngine Loader actively monitors the environment it runs in for signs of abuse
          or tampering. If it detects activity matching the categories below, it can suspend or
          ban your license <strong className="text-gray-200">automatically and immediately</strong>,
          without staff involvement at the moment of detection. We do not disclose the specific
          technical signals used, as doing so would make them trivial to evade. This protects the
          Service from abuse that would otherwise get the tool detected by the game's anti-cheat
          and ruin it for every user. Bans applied this way follow the same{' '}
          <Link to="/terms" className="link underline">no-refund policy</Link>{' '}
          as manual bans.
        </p>
      </Section>

      <Section title="Prohibited Activity">
        <p className="text-gray-500">
          Severity indicates the typical enforcement outcome. We reserve the right to act
          differently based on context.
        </p>
        <div className="surface-sunken px-4 sm:px-5 mt-3">
          <RuleItem severity="instant">
            Tampering with, debugging, or attempting to inspect the internal workings of the
            Loader while it is running.
          </RuleItem>
          <RuleItem severity="instant">
            Attempting to reverse engineer, decompile, or extract the Loader's code, license
            verification logic, or communication protocol.
          </RuleItem>
          <RuleItem severity="instant">
            Sharing, reselling, or redistributing your license key or account credentials to
            another person.
          </RuleItem>
          <RuleItem severity="instant">
            Running the Loader in an environment designed to obscure or spoof your hardware
            identity.
          </RuleItem>
          <RuleItem severity="instant">
            Interfering with the Loader's process from another unrelated application while it is
            running.
          </RuleItem>
          <RuleItem severity="warning">
            Repeatedly requesting HWID resets in a short period without a legitimate hardware
            change (suspected multi-accounting or key sharing).
          </RuleItem>
          <RuleItem severity="warning">
            Attempting to activate a license key on a device other than the one it was originally
            bound to, without going through the proper HWID reset process.
          </RuleItem>
          <RuleItem severity="review">
            Submitting an invite request with a false, misleading, or copy-pasted justification.
          </RuleItem>
          <RuleItem severity="review">
            Excessive or automated API requests outside of normal Loader or website usage
            patterns.
          </RuleItem>
        </div>
      </Section>

      <Section title="Appeals">
        <p className="text-gray-400">
          If you believe an automated or manual ban was issued in error, you may contact support
          to request a review. Reviews are handled at our discretion and are not guaranteed to
          result in reinstatement. Submitting an appeal does not pause or refund any subscription
          time lost during the ban.
        </p>
      </Section>
    </LegalLayout>
  );
}
